// ─────────────────────────────────────────────────────────────────────────────
// MoreSheetContent.tsx — "More" mobile sheet: FE pattern + backend architecture
// ─────────────────────────────────────────────────────────────────────────────

import { Code2, Server } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setFrontEndPattern, setBackEndArchitecture } from '@/store/uiSlice';
import { resetCV } from '@/store/cvSlice';
import { FE_PATTERNS, BACKENDS } from '@/data/navOptions';
import type { FrontEndPattern, BackEndArchitecture } from '@/types/cv.types';

export function MoreSheetContent() {
  const dispatch       = useAppDispatch();
  const activePattern  = useAppSelector((s) => s.ui.frontEndPattern);
  const activeBackend  = useAppSelector((s) => s.ui.backEndArchitecture);

  const handlePatternChange = (pattern: FrontEndPattern) => {
    dispatch(setFrontEndPattern(pattern));
  };

  const handleBackendChange = (arch: BackEndArchitecture) => {
    dispatch(setBackEndArchitecture(arch));
    dispatch(resetCV());
  };

  return (
    <div className="flex flex-col gap-1 px-2 pb-4">
      <p className="px-3 pt-3 pb-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-2">
        <Code2 size={12} /> F/E Pattern
      </p>
      {FE_PATTERNS.map((p) => (
        <button
          key={p.value}
          onClick={() => handlePatternChange(p.value)}
          className={`text-left px-3 py-3 rounded-xl transition-colors
            ${activePattern === p.value
              ? 'bg-accent-soft text-accent'
              : 'hover:bg-slate-50 dark:hover:bg-navy-800 text-slate-700 dark:text-slate-300'
            }`}
          role="option"
          aria-selected={activePattern === p.value}
        >
          <span className="block text-sm font-medium">{p.label}</span>
          <span className="block text-xs text-slate-400 dark:text-slate-500 mt-0.5">{p.desc}</span>
        </button>
      ))}

      <p className="px-3 pt-4 pb-1 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-2">
        <Server size={12} /> Back-End Architecture
      </p>
      {BACKENDS.map((b) => (
        <button
          key={b.value}
          onClick={() => handleBackendChange(b.value)}
          className={`text-left px-3 py-3 rounded-xl transition-colors
            ${activeBackend === b.value
              ? 'bg-accent-soft text-accent'
              : 'hover:bg-slate-50 dark:hover:bg-navy-800 text-slate-700 dark:text-slate-300'
            }`}
          role="option"
          aria-selected={activeBackend === b.value}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{b.label}</span>
            <span className="text-xs bg-navy-900/10 dark:bg-white/10 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded font-mono">{b.lang}</span>
          </div>
          <span className="block text-xs text-slate-400 dark:text-slate-500 mt-0.5">{b.desc}</span>
        </button>
      ))}
    </div>
  );
}
